import styled from '@emotion/styled'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import { SafeIcon, MobileIcon, EngagingIcon } from '@segment/matcha/components/Icons/Brandbook'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import ButtonText from '@segment/matcha/components/ButtonText'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import RealTimeAudiencesImage from './RealTimeAudiencesImage'

const copy = {
  heading: `Real-time, intelligent audiences`,
  description: `Target the right customer with the right message at the right time. Build audiences and create AI-powered real-time
campaigns, without relying on other teams.`,
}

const link = {
  text: `Explore Twilio Engage`,
  url: `/product/twilio-engage/`,
}

const features = [
  {
    heading: `Proactively personalize with Predictive Audiences`,
    description: `CustomerAI Predictions includes pre-built Predictive Audiences to inspire new campaign ideas and help you quickly create AI-powered audiences in just a few clicks.`,
    link: {
      text: `Learn more`,
      href: `/solutions/ai/customerai-predictions/`,
    },
    icon: <EngagingIcon />,
  },
  {
    heading: `Instant mobile integrations`,
    description: `Send an email or show an ad within minutes of a user performing an action.`,
    icon: <MobileIcon />,
  },
  {
    heading: `Consistent content across platforms`,
    description: `Send a consistent message across all channels — email, Facebook & Google ads, and in-product.`,
    icon: <SafeIcon />,
  },
]

const PowerYourApps = () => {
  const pageConfig = usePageConfig()

  return (
    <SectionWrapper variant="2Col" copyVariant="narrow">
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description>{copy.description}</CopyText.Description>
          <CopyText.Link href={getAbsoluteUrl(link.url, pageConfig)}>{link.text}</CopyText.Link>
        </CopyTextBase>

        <ColumnTrackWrapper>
          <ColumnTrack gap="small">
            {features.map((feature, index) => (
              <FeatureBoxBase key={index} alignment="left" headerPosition="left">
                <FeatureBox.Header>
                  <IconWrapper elevation={1} variant="circle">
                    {feature.icon}
                  </IconWrapper>
                </FeatureBox.Header>
                <FeatureBox.Body>
                  <FeatureBox.Title>{feature.heading}</FeatureBox.Title>
                  <FeatureBox.Text>{feature.description}</FeatureBox.Text>
                  {feature.link && (
                    <div>
                      <ButtonText size="small" variant="primary" href={getAbsoluteUrl(feature.link.href, pageConfig)}>
                        {feature.link.text}
                      </ButtonText>
                    </div>
                  )}
                </FeatureBox.Body>
              </FeatureBoxBase>
            ))}
          </ColumnTrack>
        </ColumnTrackWrapper>
      </Section.Copy>

      <Section.Contents>
        <RealTimeAudiencesImage />
      </Section.Contents>
    </SectionWrapper>
  )
}

const SectionWrapper = styled(SectionBase)`
  overflow-x: visible;
`

const ColumnTrackWrapper = styled.div`
  margin-top: 40px;
`

export default PowerYourApps
